import { animate, AnimationEvent, style, transition, trigger, useAnimation } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, ContentChildren, Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, Renderer2, TemplateRef, ViewChild, ViewChildren } from '@angular/core';

import { ApplicationError } from '../../../error-handler/error-handler.service';
import { linearFadeOut, slideIn } from '../../animations/animations';
import { Menu, MenuItem } from '../../models/menu.model';
import { DeviceDetector } from '../../services/device-detector.service';
import { DocumentService } from '../../services/document.service';
import { WindowService } from '../../services/window.service';
import { LocService } from '../../shared-context/loc.service';
import { KeyboardKey, KeystrokeHandler } from '../../utilities/keystroke-handler.utility';
import { MenuSelectItemEvent } from './menu-select-item.model';

// This class is a directive used in conjunction with DropdownMenuComponent
// Any content that is placed as a child of a node with this directive will be repeated in the dropdown menu as a menu item.
// Usage: *ohpDropdownMenuItemContent='let item = item; let index = index'
// Don't forget the '*' otherwise, it will not work properly!
@Directive({
    selector: '[ohpMenuItemContent]'
})
export class MenuItemContentDirective {
    constructor(
        public templateRef: TemplateRef<any>
    ) { }
}

@Component({
    selector: 'ohp-menu',
    templateUrl: './menu.component.html',
    animations: [
        trigger('openAndClose', [
            transition('* => openMobile', useAnimation(slideIn)),
            transition('* => open', [
                style({ height: 0 }),
                animate('300ms cubic-bezier(.1,.9,.2,1)', style({ height: '*' }))
            ]),
            transition('open => closing', useAnimation(linearFadeOut))
        ])
    ]
})
export class MenuComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('menuWrapper') public menuWrapper: ElementRef;

    @ViewChildren('menuItem') public dropdownItems: QueryList<ElementRef>;

    @ContentChildren(MenuItemContentDirective) public menuItems: QueryList<MenuItemContentDirective>;
    public menuItemTemplate: TemplateRef<any>;

    @Input() public menuModel: Menu;

    @Input() public animateClose: boolean;

    @Input() public mobileMenu?: boolean;

    // TODO: Add positionMenu logic to handle the menu alignment both for Office.com instead of passing through components
    @Input() public leftAlignedCard?: boolean = false;

    @Input() public multiSelect?: boolean = false;

    @Input() public contextAriaLabel?: string = '';

    @Input() public ariaRole?: string = 'listitem';

    @Input() public selectedIndex?: number;

    @Output() public readonly selectItem: EventEmitter<MenuSelectItemEvent>;

    @Output() public readonly closeMenu: EventEmitter<boolean>;

    public open: boolean;
    public animationState: 'open' | 'closing' | 'openMobile' ;
    public menuAriaLabel: string;

    private focusActive: boolean;
    private activeIndex: number;
    private menuItemClickHandler: (event: MouseEvent | TouchEvent) => void;
    private menuItemClickListener: Function;
    private menuItemTouchListener: Function;
    private menuItemContextListener: Function;
    private menuItemKeyListener: Function;
    private destroyed: boolean;
    private closeByKey: boolean;
    private closed: boolean;

    private static readonly expectedKeys = [KeyboardKey.Tab, KeyboardKey.Enter, KeyboardKey.Escape, KeyboardKey.ArrowUp, KeyboardKey.ArrowDown];

    constructor(
        private readonly deviceDetector: DeviceDetector,
        private readonly documentService: DocumentService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly locService: LocService,
        private readonly renderer: Renderer2,
        private readonly elementRef: ElementRef,
        private readonly windowService: WindowService
    ) {
        this.selectItem = new EventEmitter<MenuSelectItemEvent>();
        this.closeMenu = new EventEmitter<boolean>();
    }

    public ngOnInit() {
        this.focusActive = false;
        this.closeByKey = false;
        this.activeIndex = -1;

        this.menuItemClickHandler = (event: MouseEvent | TouchEvent) => {
            if (this.elementRef.nativeElement.contains(event.target)) {
                return;
            }
            if (this.mobileMenu) {
                event.preventDefault();
            }
            this.close();
        };

        this.animationState = this.mobileMenu ? 'openMobile' : 'open';
        this.open = true;
        this.destroyed = false;
        this.menuAriaLabel = this.locService.format(this.locService.MenuAriaLabel, this.contextAriaLabel);

        if (this.selectedIndex >= 0) {
            this.menuModel.items[this.selectedIndex].ariaChecked = true;
        }

        if (this.mobileMenu) {
            // Turn off scrolling on mobile safari
            this.documentService.disableTouchMove();
        }
    }

    public ngAfterViewInit() {
        if (this.menuItems.length !== 1) {
            throw new ApplicationError('Must have exactly 1 *ohpMenuItemContent in a Menu as a child');
        }

        this.menuItemTemplate = this.menuItems.first.templateRef;

        this.changeDetectorRef.detectChanges();

        // Skip currently propagating click event that opened the menu
        setTimeout(() => {
            // Prevent page jump on touch devices
            if (this.deviceDetector.isDesktopDevice()) {
                this.setActiveIndex(0, true);
                const menuItem = this.documentService.nativeDocument.activeElement;

                // Wait for animation to start and check if the active element is in the view.
                setTimeout(() => {
                    const menuItemPosition = menuItem.getBoundingClientRect();

                    if (menuItemPosition.top + menuItemPosition.height > this.windowService.nativeWindow.innerHeight) {
                        menuItem.scrollIntoView();
                    }
                }, 100);
            }

            if (!this.destroyed) {
                this.menuItemClickListener = this.renderer.listen(this.documentService.nativeDocument, 'click', this.menuItemClickHandler);
                this.menuItemTouchListener = this.renderer.listen(this.documentService.nativeDocument, 'touchend', this.menuItemClickHandler);
                this.menuItemContextListener = this.renderer.listen(this.documentService.nativeDocument, 'contextmenu', this.menuItemClickHandler);
                // Key events in scan mode only happens on esc key presses, but don't register on the menu component
                this.menuItemKeyListener = this.renderer.listen(this.documentService.nativeDocument, 'keydown', event => {
                    const key = KeystrokeHandler.getKey(event);
                    if (key === KeyboardKey.Escape) {
                        this.close(true);
                    }
                });
            }
        }, 0);
    }

    public ngOnDestroy() {
        this.destroyed = true;
        if (this.menuItemClickListener) {
            this.menuItemClickListener();
        }
        if (this.menuItemTouchListener) {
            this.menuItemTouchListener();
        }
        if (this.menuItemContextListener) {
            this.menuItemContextListener();
        }
        if (this.menuItemKeyListener) {
            this.menuItemKeyListener();
        }
        this.documentService.enableTouchMove();
    }

    public onAnimationCompleted(event: AnimationEvent) {
        // reset to base state at end of the animation. Avoid doing this if animation has been interrupted.
        if (event.toState === 'closing') {
            this.completeClose();
        }
    }

    public onSelectItem(item: MenuItem, index: number, nativeDomEvent: MouseEvent | KeyboardEvent): void {
        if (this.menuModel.announceItemIsSelected) {
            if (this.ariaRole === 'radio' || this.ariaRole === 'menuitemcheckbox') {
                if (this.multiSelect) {
                    const checkedItem = this.menuModel.items.find(menuItem => item.getId() === menuItem.getId());
                    checkedItem.ariaChecked = !checkedItem.ariaChecked;
                } else {
                    this.menuModel.items.forEach(menuItem => menuItem.ariaChecked = item.getId() === menuItem.getId());
                }
            } else {
                this.menuModel.items.forEach(menuItem => menuItem.ariaSelected = item.getId() === menuItem.getId());
            }
        }

        this.selectItem.emit({ item, index, nativeDomEvent });
        nativeDomEvent.preventDefault();

        if (!this.multiSelect) {
            this.close();
        }
    }

    public close(closeByKey: boolean = false) {
        if (!this.closed) {
            this.closed = true;
            this.closeByKey = closeByKey;
            if (this.animateClose) {
                this.animationState = 'closing';
                return;
            }

            this.completeClose();
        }
    }

    public handleKey(event: KeyboardEvent) {
        const key = KeystrokeHandler.getKey(event);

        if (MenuComponent.expectedKeys.indexOf(key) === -1) {
            return;
        }

        event.preventDefault();

        // Enter key selects the active item
        if (key === KeyboardKey.Enter && this.activeIndex >= 0) {
            const selectedItem = this.menuModel.items[this.activeIndex];
            this.onSelectItem(selectedItem, this.activeIndex, event);
            return;
        }

        const limit = this.menuModel.items.length - 1;
        switch (key) {
            case KeyboardKey.Tab:
            case KeyboardKey.Escape:
                // Return focus to button that opened menu
                this.close(true);
                break;
            case KeyboardKey.ArrowUp:
                this.focusActive = true;
                this.setActiveIndex(this.activeIndex > 0 ? this.activeIndex - 1 : limit);
                break;
            case KeyboardKey.ArrowDown:
                this.focusActive = true;
                this.setActiveIndex((this.activeIndex + 1) % (limit + 1));
                break;
        }
    }

    public setActiveIndex(index: number, forceFocus?: boolean) {
        if (forceFocus) {
            this.focusActive = true;
        }
        this.activeIndex = index;
        if (this.focusActive) {
            const focusedItem = this.dropdownItems.find(item => item.nativeElement.id === this.getIdForItem(this.menuModel.items[index]));
            if (focusedItem) {
                focusedItem.nativeElement.focus();
            }
        }
    }

    public getIdForItem(item: MenuItem): string {
        return `${this.menuModel.id}-item--${item.getId()}`;
    }

    public trackById(_index: number, item: MenuItem) {
        return item.getId();
    }

    private completeClose() {
        this.open = false;
        this.documentService.enableTouchMove();
        this.closeMenu.emit(this.closeByKey);
        this.closeByKey = false;
    }
}
